/**
 * Web Crypto helpers shared across runtime targets (Cloudflare Workers, Node
 * 19+, browsers). Built on `crypto.subtle` so the same implementation runs
 * inside Workers without a Node-only `node:crypto` import.
 */

import { v4 as uuidv4, v5 as uuidv5 } from "uuid";

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

export function base64UrlToBytes(input: string): Uint8Array {
  const normalized = input.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function randomBase64Url(byteLength: number): string {
  return bytesToBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

/**
 * Prefer this over the global `crypto.randomUUID()`, which is secure-context-only
 * and therefore undefined over plain `http://`. `uuid`'s v4 is backed by
 * `crypto.getRandomValues`, which carries no such restriction.
 */
export function randomUUID(): string {
  return uuidv4();
}

/** Strings use UTF-8; binary inputs are hashed unchanged. */
export async function sha256Hex(
  input: string | Parameters<typeof crypto.subtle.digest>[1],
): Promise<string> {
  const buffer =
    typeof input === "string" ? new TextEncoder().encode(input) : input;
  const hash = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(hash)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function constantTimeEqual({
  left,
  right,
}: {
  left: Uint8Array;
  right: Uint8Array;
}): boolean {
  if (left.length !== right.length) {
    return false;
  }

  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }
  return difference === 0;
}

export async function getHmacSha256Hex({
  rawBody,
  signingKey,
}: {
  rawBody: string;
  signingKey: string;
}): Promise<string> {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(signingKey),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    encoder.encode(rawBody),
  );
  return [...new Uint8Array(signature)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function isValidHmacSha256Hex({
  rawBody,
  signingKey,
  signature,
}: {
  rawBody: string;
  signingKey: string;
  signature: string;
}): Promise<boolean> {
  const encoder = new TextEncoder();
  return constantTimeEqual({
    left: encoder.encode(await getHmacSha256Hex({ rawBody, signingKey })),
    right: encoder.encode(signature),
  });
}

export function deterministicUUID(value: string): string {
  return uuidv5(value, uuidv5.URL);
}
